import { Actions, Async, F1 } from '@client/new/helpers/types';
import _ from 'lodash';
import {
  useGetDirectMessageStatusQuery,
  useSetDirectMessagingMutation,
} from 'generated/graphql';
import toast from 'react-hot-toast';
import { useEffect, useState } from 'react';
import auth from '@client/components/auth';
import { ChatApi } from '@client/api/ChatApi';

export type InboxModelParams = {
  influencerId: number;
};

export type InboxModel = {
  dispatch: InboxViewDispatcher;
  submitting: boolean;
  influencerId: number;
  streamToken: string | null;
  userId: string;
  canDirectMessage: boolean;
  offering: Array<string>;
  premiumFeaturesOTPIds: Array<number | null>;
};

export type InboxActionsMap = {
  submitEnable: null;
};

export type InboxActions = Actions<InboxActionsMap>;

export type InboxViewDispatcher = F1<InboxActions>;

export const useInboxModel = (p: InboxModelParams): Async<InboxModel> => {
  const [submitting, setSubmitting] = useState(false);
  const [setDirectMessagingMutation] = useSetDirectMessagingMutation();
  const [streamToken, setStreamToken] = useState(null);

  const { data, loading, error, refetch } = useGetDirectMessageStatusQuery({
    fetchPolicy: 'network-only',
    variables: { id: p.influencerId },
    skip: !p.influencerId,
  });

  const canDirectMessage = !!data?.creator?.customizations?.canDirectMessage;

  useEffect(() => {
    if (!streamToken && canDirectMessage) {
      const isCreator = auth.isInfluencer();
      if (isCreator) {
        ChatApi.getCreatorChatToken().then((token) => {
          setStreamToken(token);
        });
      } else {
        ChatApi.getModeratorChatToken({ creatorId: p.influencerId }).then(
          (token) => {
            setStreamToken(token);
          },
        );
      }
    }
  }, [canDirectMessage]);

  const userId = `creator-${p.influencerId}`;

  if (loading || (canDirectMessage && !streamToken)) {
    return {
      type: 'fetching',
    };
  }

  if (error) {
    return {
      type: 'fetch-error',
      value: 'Could not fetch data',
    };
  }

  const onEnableDirectMessaging = async () => {
    setSubmitting(true);
    try {
      await setDirectMessagingMutation({
        variables: {
          data: {
            influencerId: p.influencerId,
            isEnabled: true,
          },
        },
      });
      await refetch();
      toast.success('Direct Messaging is now available!');
    } catch (e) {
      toast.error('Something went wrong!');
    } finally {
      setSubmitting(false);
    }
  };

  const dispatch: InboxViewDispatcher = (action) => {
    switch (action.type) {
      case 'submitEnable':
        onEnableDirectMessaging();
        return null;
    }
  };

  return {
    type: 'fetched',
    value: {
      dispatch,
      submitting,
      influencerId: p.influencerId,
      streamToken,
      userId,
      canDirectMessage: !!data?.creator?.customizations?.canDirectMessage,
      premiumFeaturesOTPIds:
        data?.creator?.customizations?.premiumFeaturesOTPIds || [],
      offering: data?.creator?.offering || [],
    },
  };
};
